import { useContext, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, IconButton, MenuItem, Stack, Switch,
  FormControlLabel, TextField, Typography, Button,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { DialogWrapper } from '../display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import CapitalGoodService from '../../../shared/services/capital-good/capital-good-service';
import { ICapitalGood } from '../../../shared/services/capital-good/capital-good.model';
import { IEpd } from '../../../shared/services/capital-good/epd.model';

interface EpdManagerDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  good: ICapitalGood;
}

const emptyForm = {
  standard: 'EN15804',
  registrationNumber: '',
  programOperator: '',
  issueDate: '',
  validUntil: '',
  isCradleToGate: true,
  gwpTotal: '',
  a1a3Gwp: '',
  gwpBiogenic: '',
  gwpVersion: 'AR5',
};

export const EpdManagerDialog: React.FC<EpdManagerDialogProps> = ({ open, onClose, onChanged, good }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const capitalGoodService = new CapitalGoodService();

  const [epds, setEpds] = useState<IEpd[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [declaredUnitValue, setDeclaredUnitValue] = useState<string>(String(good.unitAmount ?? 1));
  const [applyToGood, setApplyToGood] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasUnit = Boolean(good.unitType);

  const fetchEpds = () => {
    setLoadingList(true);
    capitalGoodService.getEpds(good.id)
      .then((rows) => setEpds(rows))
      .catch(() => setEpds([]))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    if (open) {
      fetchEpds();
      setFile(null);
      setForm({ ...emptyForm });
      setDeclaredUnitValue(String(good.unitAmount ?? 1));
      setApplyToGood(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, good.id]);

  const setField = (key: keyof typeof emptyForm, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const gwpField = form.isCradleToGate ? form.gwpTotal : form.a1a3Gwp;
  const canSubmit = Boolean(file) && form.standard !== '' && form.registrationNumber.trim() !== '' && gwpField !== '';

  const handleSubmit = async () => {
    if (!file) return;
    try {
      setIsSubmitting(true);
      const meta = {
        standard: form.standard,
        registrationNumber: form.registrationNumber.trim(),
        programOperator: form.programOperator.trim() || undefined,
        issueDate: form.issueDate || null,
        validUntil: form.validUntil || null,
        isCradleToGate: form.isCradleToGate,
        lifeCycleModules: form.isCradleToGate ? 'A1-A3' : 'A1-C',
        declaredUnitValue: Number(declaredUnitValue) || 1,
        declaredUnitMeasurementUnitId: good.unitType,
        gwpTotal: form.isCradleToGate ? Number(form.gwpTotal) : null,
        a1a3Gwp: !form.isCradleToGate && form.a1a3Gwp !== '' ? Number(form.a1a3Gwp) : null,
        gwpBiogenic: form.gwpBiogenic !== '' ? Number(form.gwpBiogenic) : null,
        gwpVersion: form.gwpVersion || undefined,
        applyToGood,
      };
      const fd = new FormData();
      fd.append('files', file);
      fd.append('epd', JSON.stringify(meta));
      const result = await capitalGoodService.uploadEpd(good.id, fd);
      userNotificationsContext.openSnackbar({
        open: true,
        message: result.message + (result.gatingReason ? ` (${result.gatingReason})` : ''),
        backgroundColor: result.applied ? 'primary.dark' : 'text.primary',
      });
      setFile(null);
      setForm({ ...emptyForm });
      fetchEpds();
      onChanged();
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'Could not upload the EPD',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (epd: IEpd) => {
    try {
      const blob = await capitalGoodService.downloadEpd(good.id, epd.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = epd.document?.filename || 'epd.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not download the EPD', backgroundColor: 'text.primary' });
    }
  };

  const handleDelete = async (epd: IEpd) => {
    try {
      await capitalGoodService.deleteEpd(good.id, epd.id);
      fetchEpds();
      onChanged();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not remove the EPD', backgroundColor: 'text.primary' });
    }
  };

  const tierColor = (tier: string | null) => (tier === 'High' ? 'success' : tier === 'Medium' ? 'warning' : 'default');

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Environmental Product Declarations"
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!canSubmit || !hasUnit}
      maxWidth="sm"
    >
      {!hasUnit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Set this product's unit and emission factor first: an EPD's verified factor is applied per the product's unit.
        </Alert>
      )}

      {/* Existing EPDs */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}><b>Attached EPDs</b></Typography>
      {loadingList ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
      ) : epds.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No EPDs attached yet.</Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {epds.map((epd) => (
            <Box key={epd.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{epd.document?.filename || epd.registrationNumber || 'EPD'}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                  <Chip size="small" label={epd.standard || '—'} variant="outlined" />
                  {epd.dataQualityTier && <Chip size="small" color={tierColor(epd.dataQualityTier)} label={`${epd.dataQualityTier} quality`} />}
                  {epd.appliedToGood && <Chip size="small" color="primary" label="Applied" />}
                </Stack>
                {epd.gatingReason && <Typography variant="caption" color="warning.main">{epd.gatingReason}</Typography>}
              </Box>
              <IconButton size="small" onClick={() => handleDownload(epd)}><DownloadOutlinedIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => handleDelete(epd)}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Upload new EPD */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}><b>Attach a new EPD</b></Typography>
      <Stack spacing={2}>
        <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon />}>
          {file ? file.name : 'Choose PDF'}
          <input type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Button>

        <TextField select size="small" label="Standard" value={form.standard} onChange={(e) => setField('standard', e.target.value)}>
          <MenuItem value="EN15804">EN 15804</MenuItem>
          <MenuItem value="ISO14025">ISO 14025</MenuItem>
        </TextField>
        <TextField size="small" label="Registration number" value={form.registrationNumber} onChange={(e) => setField('registrationNumber', e.target.value)} />
        <TextField size="small" label="Programme operator" value={form.programOperator} onChange={(e) => setField('programOperator', e.target.value)} />

        <FormControlLabel
          control={<Switch checked={form.isCradleToGate} onChange={(e) => setField('isCradleToGate', e.target.checked)} />}
          label="EPD is cradle-to-gate (A1–A3 only)"
        />
        {form.isCradleToGate ? (
          <TextField size="small" type="number" label="GWP-total A1–A3 (kg CO₂e per declared unit)" value={form.gwpTotal} onChange={(e) => setField('gwpTotal', e.target.value)} />
        ) : (
          <TextField size="small" type="number" label="A1–A3 sub-total (kg CO₂e per declared unit)" helperText="Cradle-to-grave totals can't be used for purchased/capital goods, so enter the A1–A3 portion." value={form.a1a3Gwp} onChange={(e) => setField('a1a3Gwp', e.target.value)} />
        )}
        <TextField size="small" type="number" label="Biogenic GWP (optional, per declared unit)" value={form.gwpBiogenic} onChange={(e) => setField('gwpBiogenic', e.target.value)} />

        <Stack direction="row" spacing={2}>
          <TextField size="small" type="number" label="Declared unit qty" value={declaredUnitValue} onChange={(e) => setDeclaredUnitValue(e.target.value)} sx={{ flex: 1 }} />
          <TextField size="small" label="Unit" value={good.measurementUnit?.name || ''} disabled sx={{ flex: 1 }} />
        </Stack>

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Stack direction="row" spacing={2}>
            <DatePicker
              label="Issued"
              format="DD/MM/YYYY"
              value={form.issueDate ? moment(form.issueDate) : null}
              onChange={(date) => setField('issueDate', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
            />
            <DatePicker
              label="Valid until"
              format="DD/MM/YYYY"
              value={form.validUntil ? moment(form.validUntil) : null}
              onChange={(date) => setField('validUntil', date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
              slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
            />
          </Stack>
        </LocalizationProvider>
        <TextField select size="small" label="GWP version" value={form.gwpVersion} onChange={(e) => setField('gwpVersion', e.target.value)}>
          <MenuItem value="AR6">IPCC AR6</MenuItem>
          <MenuItem value="AR5">IPCC AR5</MenuItem>
          <MenuItem value="AR4">IPCC AR4</MenuItem>
        </TextField>

        <FormControlLabel
          control={<Switch checked={applyToGood} onChange={(e) => setApplyToGood(e.target.checked)} />}
          label="Use this EPD as the product's emission factor"
        />
      </Stack>
    </DialogWrapper>
  );
};
